<template>
  <div class="page">

    <div class="page__subheader">
      <ui-link-button-meek
        :size="UI.SIZE.SMALL"
        :route="parentRoute"
      >
        <ui-icon
          name="navigate_before"
        />
        Projets
      </ui-link-button-meek>
    </div>

    <div class="page__main">

      <ui-cloak
        :is-visible="fetchState.status.value === 'success'"
        :sustain-phase-duration="800"
      >
        <template #cloak>
          <b-card class="card--leveled">
            <hamac-project-create-placeholder />
          </b-card>
        </template>

        <template #default>
          <b-card class="card--leveled">
            <create-organism
              v-bind="{
                searchTeamCallback,
                searchTopicCallback,
                searchProfessionCallback,
                searchAnalyticCallback,
                searchTrackingNumberCallback,
                createTrackingNumberCallback,
                createAnalyticCallback,
                isPending,
                submitCallback: createCallback,
                teamTagList,
                customDataList,
                isStructureConnected,
              }"
            />
          </b-card>
        </template>

      </ui-cloak>

    </div>

  </div>
</template>

<style lang="sass" scoped>
@import '~/assets/sass/modules/layout/variables'

$form-width: 880px

.page
  padding: ($layout-spacer-y) ($layout-spacer-x * 1.5)
  background-color: $background-tertiary

  &__subheader
    margin-bottom: $layout-spacer-y * .75

  &__main
    max-width: $form-width

</style>

<script lang="ts">
import CreateOrganism from '~/components/hamac/project/create/Organism.vue'
import Console from '~/utils/lib/core/console'
import { delay, join, noop, tapCatch, tapThen, toString } from '~/utils/lib/core/prelude'
import { MENU_ITEM, NAVIGATION_SCOPE } from '~/utils/lib/layout/types'
import { getListRoute, getDetailsRoute } from '~/stores/hamac/project/index'
import { useLegacyPageFetch } from '~/composables/core/hook'
import { useToastBuilder } from '~/composables/layout/toast'
import { useCompanyRequiredNavigationContext } from '~/composables/layout/navigation'
import { useLinkHandler } from '~/composables/core/link-handler'
import { wrap } from '~/utils/infra/common/types'
import { useStore_hamac_team_list, type Filters, type Team } from '~/stores/hamac/team/list'
import { useStore_hamac_contractTemplate_customData, type CustomData } from '~/stores/hamac/contract-template/custom-data'
import type { Context } from '~/composables/layout/table-provider'
import { useStore_hamac_topic_company_list } from '~/stores/hamac/topic/company/list'
import { useStore_hamac_profession_list } from '~/stores/hamac/profession/list'
import { useStore_hamac_analytic_list } from '~/stores/hamac/analytic/list'
import { useStore_hamac_trackingNumber } from '~/stores/hamac/tracking-number'
import { useStore_hamac_analytic } from '~/stores/hamac/analytic'
import { useStore_hamac_project_details } from '~/stores/hamac/project/details'
import { useStore_hamac_team_tag } from '~/stores/hamac/team/tag'
import { useStore_hamac_structure_ping } from '~/stores/hamac/structure/ping'
import { useStore_hamac_trackingNumber_list } from '~/stores/hamac/tracking-number/list'

const console = new Console('component:project-create')

export default defineComponent({
  components: { CreateOrganism },
  setup() {
    definePageMeta({
      navigationScope: NAVIGATION_SCOPE.COMPANY_REQUIRED,
      menuItem: MENU_ITEM.EMBAUCHE,
    })
    const navigationContext = useCompanyRequiredNavigationContext()

    useHead({
      title: join(' ', [
        `Projet → ajout`,
        `— ${navigationContext.fromCompanyLevel().company.raison_sociale}`,
      ]),
    })

    const {
      fetchState,
      fetchWrapper,
    } = useLegacyPageFetch()

    const ToastBuilder = useToastBuilder()

    const parentRoute = getListRoute(
      navigationContext.fromCompanyLevel().structure.id,
      navigationContext.fromCompanyLevel().company.code_societe
    )

    const store_team_list = useStore_hamac_team_list()
    const store_profession_list = useStore_hamac_profession_list()
    const store_analytic_list = useStore_hamac_analytic_list()
    const store_analytic = useStore_hamac_analytic()
    const store_topic_company_list = useStore_hamac_topic_company_list()
    const store_trackingNumber_list = useStore_hamac_trackingNumber_list()
    const store_trackingNumber = useStore_hamac_trackingNumber()
    const store_project_details = useStore_hamac_project_details()
    const store_team_tag = useStore_hamac_team_tag()
    const store_contractTemplate_customData = useStore_hamac_contractTemplate_customData()
    const store_structure_ping = useStore_hamac_structure_ping()

    return {
      ...navigationContext,
      ...useLinkHandler(),
      fetchState,
      fetchWrapper,
      ToastBuilder,
      parentRoute,
      store_team_list,
      store_topic_company_list,
      store_profession_list,
      store_analytic_list,
      store_trackingNumber,
      store_analytic,
      store_project_details,
      store_team_tag,
      store_contractTemplate_customData,
      store_structure_ping,
      store_trackingNumber_list,
    }
  },
  data() {
    return {
      isPending: false,
      teamTagList: [] as Team["tags"], // lazy
      customDataList: [] as CustomData[], // lazy,
      isStructureConnected: false,
    }
  },
  created() {
    this.fetchWrapper(
      () => this.fetch()
    )
  },
  methods: {
    searchTeamCallback(context :Context<Filters>) {
      const opts = {
        input: wrap(context),
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe
      }

      return this.store_team_list.read(opts)
        .catch(tapCatch(err => {
          console.warn('searchTeamCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Recherche d'équipes`)
            .show()
        }))
    },
    searchTopicCallback(context) {
      const opts = {
        input: wrap(context),
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe
      }

      return this.store_topic_company_list.read(opts)
        .catch(tapCatch(err => {
          console.warn('searchTopicCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Recherche de rubriques`)
            .show()
        }))
    },
    searchProfessionCallback(context) {
      const opts = {
        input: wrap(context),
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe
      }

      return this.store_profession_list.read(opts)
        .catch(tapCatch(err => {
          console.warn('searchProfessionCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Recherche de professions`)
            .show()
        }))
    },
    searchAnalyticCallback(context) {
      const opts = {
        input: wrap(context),
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe
      }

      return this.store_analytic_list.read(opts)
        .catch(tapCatch(err => {
          console.warn('searchAnalyticCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Recherche d'analytiques`)
            .show()
        }))
    },
    searchTrackingNumberCallback(context) {
      const opts = {
        input: wrap(context),
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe
      }

      return this.store_trackingNumber_list.read(opts)
        .catch(tapCatch(err => {
          console.warn('searchTrackingNumberCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Recherche de numéro d'objet`)
            .show()
        }))
    },
    createTrackingNumberCallback(data) {
      const opts = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
        data
      }

      return this.store_trackingNumber.create(opts)
        .then(tapThen(({ numero_objet }) => {
          // ⚠ Defensive delay to make sure the toast is visible after the modals are closed
          delay(300)
            .then(() => {
              this.ToastBuilder
                .success(`Le numéro d'objet « ${numero_objet} » a bien été ajouté au projet`)
                .class('create-tracking-number-toast')
                .title(`Numéro d'objet ajouté`)
                .show()
            })
        }))
        .catch(tapCatch(err => {
          console.warn('createTrackingNumberCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .class('create-tracking-number-toast')
            .title(`Ajout d'un numéro d'objet`)
            .show()
        }))
    },
    createAnalyticCallback(data) {
      const opts = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
        uuid_hamac_projet: this.$route.params.projectCode,
        data
      }

      return this.store_analytic.create(opts)
    },
    createCallback(data) {
      const opts = {
        data,
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
      }

      this.isPending = true

      return this.store_project_details.create(opts)
        .then(({ uuid_hamac_projet }) => {
          const {
            structure: { id },
            company: { code_societe }
          } = this.fromCompanyLevel()
          const url = getDetailsRoute(id, code_societe, uuid_hamac_projet)

          this.goToRoute(url)
            .then(
              () => this.ToastBuilder
                .success()
                .class('create-project-toast')
                .title(`Projet ajouté !`)
                .show()
            )
            .catch(noop)
        })
        .catch(err => {
          this.isPending = false

          console.warn('create', err)
          this.ToastBuilder
            .error(toString(err))
            .class('create-project-toast')
            .title(`Ajout projet`)
            .show()
        })
    },
    fetch() {
      const options = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
        filters: { perPage: 10000 }  // eager
      }

      return Promise.all([
        this.store_team_tag.read(options),
        this.store_contractTemplate_customData.read_(
          { uuidData: options.uuidData }
        ),
        this.store_structure_ping.exec_(
          { ...options }
        ),
      ])
      .then(res => {
        this.teamTagList = res[0]
        this.customDataList = res[1]
        this.isStructureConnected = res[2]
      })
      .catch(tapCatch(err => {
        console.warn('fetch', err)
        this.ToastBuilder
          .error(toString(err))
          .title(`Ajout projet`)
          .show()
      }))
    }
  }
})
</script>
